import { ChipProps } from '@c2fo/liquidity';
import { AdjustmentInvoice, AdjustmentInvoiceSearchFilters } from '@/enterprise/data/useAdjustmentInvoices';
import { AdjustmentInvoiceStatsSearchFilters } from '@/enterprise/data/useAdjustmentInvoiceStats';
import { AdjustmentFilterOptions } from './AdjustmentFilters';
import { AdjustmentUrlState } from './Adjustments';

export const adjustmentTypeConfig: Record<
  AdjustmentInvoice['status'],
  { labelKey: string; chipType: ChipProps['type']; tooltipKey: string }
> = {
  matched: {
    labelKey: 'taker.invoices.adjustment.statusMatched',
    chipType: 'default',
    tooltipKey: 'invoiceCentral.matched.tooltip',
  },
  unmatched: {
    labelKey: 'taker.invoices.adjustment.statusUnmatched',
    chipType: 'default',
    tooltipKey: 'invoiceCentral.unmatched.tooltip',
  },
};

export const mergeFilters = ({
  prevFilters,
  newFilters,
}: {
  prevFilters?: AdjustmentFilterOptions;
  newFilters?: AdjustmentFilterOptions;
}) => {
  if (!newFilters) {
    return prevFilters ?? {};
  }

  let mergedFilters = prevFilters ?? {};

  // Helper function to merge filters with range values (from/to)
  const mergeRangeFilter = <K extends Exclude<keyof AdjustmentFilterOptions, 'adjustmentType'>>(filterKey: K) => {
    if (!newFilters[filterKey]) {
      return;
    }

    // Date filters from/to properties are strings, but amount is number
    // Create type parameter for the filter type in this call
    type FilterType = AdjustmentFilterOptions[K];
    const newFilter = newFilters[filterKey] as FilterType;
    const { [filterKey]: prevFilter, ...rest } = mergedFilters;

    let mergedFilter = newFilter;
    if (prevFilter) {
      mergedFilter = {
        ...(newFilter?.from !== null && { from: newFilter?.from ?? prevFilter.from }),
        ...(newFilter?.to !== null && { to: newFilter?.to ?? prevFilter.to }),
      } as FilterType;
    }

    mergedFilters = {
      ...rest,
      ...((mergedFilter?.from || mergedFilter?.to) && { [filterKey]: mergedFilter }),
    };
  };

  mergeRangeFilter('originalDepositDate');
  mergeRangeFilter('amount');

  if (newFilters.adjustmentType) {
    const { adjustmentType: prevAdjustmentType, ...rest } = mergedFilters;

    const mergedAdjustmentType = {
      ...prevAdjustmentType,
      ...newFilters.adjustmentType,
    };

    // only pass through adjustmentType properties if they are true
    const cleanedAdjustmentType = Object.fromEntries(
      Object.entries(mergedAdjustmentType).filter(([_, value]) => value)
    );

    mergedFilters = {
      ...rest,
      ...(Object.keys(cleanedAdjustmentType).length > 0 && { adjustmentType: cleanedAdjustmentType }),
    };
  }

  return mergedFilters;
};

export const mapUrlStateToApiSearchFilters = ({
  urlState,
}: {
  urlState: AdjustmentUrlState;
}): AdjustmentInvoiceSearchFilters | AdjustmentInvoiceStatsSearchFilters => {
  const search = {
    // Even though searchValue is typed as string, useUrlState is parsing it as a number when it's just digits
    // e.g. "123" becomes 123, so cast it to string for API
    ...(urlState.searchValue && { voucherPatterns: [String(urlState.searchValue)] }),
    ...((!!urlState.filters?.amount?.from || !!urlState.filters?.amount?.to) && {
      amount: [
        {
          ...(!!urlState.filters.amount?.from && { gte: Number(urlState.filters.amount.from) }),
          ...(!!urlState.filters.amount?.to && { lte: Number(urlState.filters.amount.to) }),
        },
      ],
    }),
    ...((!!urlState.filters?.originalDepositDate?.from || !!urlState.filters?.originalDepositDate?.to) && {
      originalDepositDate: [
        {
          ...(!!urlState.filters.originalDepositDate?.from && { from: urlState.filters.originalDepositDate.from }),
          ...(!!urlState.filters.originalDepositDate?.to && { to: urlState.filters.originalDepositDate.to }),
        },
      ],
    }),
    ...((!!urlState.filters?.adjustmentType?.matched || !!urlState.filters?.adjustmentType?.unmatched) && {
      status: [
        ...(!!urlState.filters.adjustmentType?.matched ? ['matched'] : []),
        ...(!!urlState.filters.adjustmentType?.unmatched ? ['unmatched'] : []),
      ] as AdjustmentInvoice['status'][],
    }),
  };

  return Object.keys(search).length > 0 ? search : undefined;
};
